// =============================================================================
// TemplateEditModal.tsx
//
// Lets the current user (Shannon or any other finance person) tweak their
// personal copy of the email subject / email body / SMS body. Saves to
// invoice_template_overrides keyed by user_id. System defaults in
// finance_settings stay untouched.
//
// Reset-to-default button clears the override (sets fields back to system).
// =============================================================================

import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

type TemplateSet = {
  emailSubject: string;
  emailBody: string;
  smsBody: string;
  isPersonalOverride: boolean;
};

type Props = {
  supabase: SupabaseClient;
  currentUserId: string;
  initial: TemplateSet;
  previewVars: Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
};

const mergeTemplate = (tpl: string, vars: Record<string, string>) =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);

export function TemplateEditModal({ supabase, currentUserId, initial, previewVars, onClose, onSaved }: Props) {
  const [subj, setSubj] = useState(initial.emailSubject);
  const [body, setBody] = useState(initial.emailBody);
  const [sms, setSms]   = useState(initial.smsBody);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      const { error } = await supabase
        .from("invoice_template_overrides")
        .upsert({
          user_id: currentUserId,
          email_subject: subj,
          email_body: body,
          sms_body: sms,
          updated_at: new Date().toISOString(),
        }, { onConflict: "user_id" });
      if (error) throw error;
      onSaved();
    } catch (e: any) {
      setErr(`Save failed: ${e?.message ?? e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!confirm("Clear your personal template and go back to the system default?")) return;
    setSaving(true);
    setErr(null);
    try {
      const { error } = await supabase
        .from("invoice_template_overrides")
        .delete()
        .eq("user_id", currentUserId);
      if (error) throw error;
      onSaved();
    } catch (e: any) {
      setErr(`Reset failed: ${e?.message ?? e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="br-modal-overlay" onClick={onClose}>
      <div className="br-modal" onClick={(e) => e.stopPropagation()}>
        <div className="br-modal-head">
          <h3>Edit your invoice templates</h3>
          <button className="br-x" onClick={onClose}>✕</button>
        </div>
        <div className="br-modal-body">
          <p className="br-hint">
            These are <b>your personal</b> default templates. The system default stays untouched —
            other finance users (if any) see their own version. Use <code>&#123;first_name&#125;</code>,
            <code> &#123;balance_due&#125;</code>, <code> &#123;invoice_link&#125;</code>,
            <code> &#123;sender_name&#125;</code>, <code> &#123;final_invoice&#125;</code>,
            <code> &#123;supplement_approved_amount&#125;</code> as merge fields.
          </p>

          <div className="br-section">
            <label>📧 Email subject</label>
            <input className="br-input" value={subj} onChange={(e) => setSubj(e.target.value)} />
            <div className="br-rendered"><span className="br-rendered-label">Preview:</span> {mergeTemplate(subj, previewVars)}</div>
          </div>

          <div className="br-section">
            <label>📧 Email body</label>
            <textarea className="br-textarea" rows={14} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>

          <div className="br-section">
            <label>💬 SMS body</label>
            <textarea className="br-textarea" rows={5} value={sms} onChange={(e) => setSms(e.target.value)} />
            <div className="br-rendered"><span className="br-rendered-label">Preview:</span> {mergeTemplate(sms, previewVars)}</div>
            <div className="br-meta">{sms.length} characters · {Math.ceil(sms.length / 160)} SMS segment(s)</div>
          </div>

          {err && <div className="br-err">{err}</div>}
        </div>
        <div className="br-modal-foot">
          {initial.isPersonalOverride && (
            <button className="br-btn-reset" onClick={handleResetToDefault} disabled={saving}>
              ↺ Reset to system default
            </button>
          )}
          <div className="br-foot-spacer" />
          <button className="br-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="br-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save my template"}
          </button>
        </div>
      </div>
      <style>{`
        .br-modal-overlay {
          position: fixed; inset: 0; background: rgba(42,46,48,0.55);
          display: flex; align-items: flex-start; justify-content: center;
          padding: 40px 20px; z-index: 100; overflow-y: auto;
        }
        .br-modal {
          background: white; width: 100%; max-width: 720px;
          border-radius: 14px; box-shadow: 0 12px 32px rgba(0,0,0,0.2);
          overflow: hidden;
        }
        .br-modal-head {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 22px; border-bottom: 1px solid #e3e6e8;
        }
        .br-modal-head h3 { margin: 0; font-size: 17px; font-weight: 700; }
        .br-x { background: none; border: none; font-size: 20px; color: #9aa0a4; cursor: pointer; }
        .br-modal-body { padding: 22px; }
        .br-hint {
          background: #fff1ea; border: 1px solid #ff9e7a; border-radius: 8px;
          padding: 10px 14px; font-size: 13px; color: #2a2e30; line-height: 1.5;
          margin: 0 0 18px;
        }
        .br-hint code { background: white; padding: 1px 5px; border-radius: 3px; font-size: 12px; color: #f46530; font-weight: 600; }
        .br-section { margin-bottom: 18px; }
        .br-section label {
          display: block; font-size: 11px; font-weight: 700;
          color: #5b6266; text-transform: uppercase; letter-spacing: 0.5px;
          margin-bottom: 6px;
        }
        .br-input, .br-textarea {
          width: 100%; padding: 10px 12px;
          border: 1px solid #e3e6e8; border-radius: 8px;
          font-size: 14px; font-family: inherit; color: #2a2e30;
          line-height: 1.5; resize: vertical;
        }
        .br-textarea { white-space: pre-wrap; }
        .br-input:focus, .br-textarea:focus { outline: 2px solid #f46530; outline-offset: -1px; border-color: #f46530; }
        .br-rendered {
          margin-top: 6px; padding: 8px 10px; background: #f3f5f6;
          border-radius: 6px; font-size: 12px; color: #2a2e30; line-height: 1.5;
          white-space: pre-wrap;
        }
        .br-rendered-label { color: #5b6266; font-weight: 700; text-transform: uppercase; font-size: 10px; letter-spacing: 0.4px; }
        .br-meta { font-size: 11px; color: #9aa0a4; margin-top: 4px; }
        .br-err {
          padding: 10px 14px; background: #fff1ea;
          border: 1px solid #ff9e7a; border-radius: 8px;
          font-size: 13px; color: #2a2e30;
        }
        .br-modal-foot {
          display: flex; align-items: center; gap: 8px;
          padding: 14px 22px; background: #f3f5f6;
          border-top: 1px solid #e3e6e8;
        }
        .br-foot-spacer { flex: 1; }
        .br-btn-reset {
          background: white; color: #5b6266;
          border: 1px solid #e3e6e8; padding: 9px 14px;
          border-radius: 8px; font-weight: 600; font-size: 13px; cursor: pointer;
        }
        .br-btn-reset:hover { border-color: #f43f5e; color: #f43f5e; }
        .br-btn-secondary {
          background: white; color: #5b6266;
          border: 1px solid #e3e6e8; padding: 10px 18px;
          border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer;
        }
        .br-btn-primary {
          background: #f46530; color: white;
          border: none; padding: 10px 22px;
          border-radius: 8px; font-weight: 700; font-size: 14px; cursor: pointer;
        }
        .br-btn-primary:disabled, .br-btn-secondary:disabled, .br-btn-reset:disabled { opacity: 0.55; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
